import React, { useEffect, useRef } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { Link } from "react-router-dom";
import Header from "../../../../Navbar/Navbar";
import Footer from "../footer/Footer";
import "../../home-theme.css";
import "./Plans.css";

/* Subscribe buttons point to the Register page — same target the navbar CTA
   and the old Plans buttons used. */
const REGISTER_PATH = `${process.env.PUBLIC_URL || ""}/auth/register/form`;

/* ------------------------------------------------------------------
   Inline tick icons (stroke = currentColor) — mirrors Carousel's Icon
------------------------------------------------------------------ */
const Check = () => (
  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);
const Cross = () => (
  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);

/* ------------------------------------------------------------------
   Data — single source of truth for each tier. Feature copy is drawn
   from the real platform modules (see Carousel.js) so the tiers read
   as a believable ERP ladder.
------------------------------------------------------------------ */
const PLANS = [
  {
    id: "trial",
    name: "Trial",
    price: "Free",
    period: "for 14 days",
    featured: false,
    blurb: "Explore the full platform on sample data — no card required.",
    features: [
      { ok: true, text: "Financial Accounting (read-only)" },
      { ok: true, text: "Unified dashboard preview" },
      { ok: true, text: "1 user seat" },
      { ok: true, text: "Sample-data sandbox" },
      { ok: false, text: "Human Capital & Payroll" },
      { ok: false, text: "Sales, POS & Inventory" },
      { ok: false, text: "Procurement" },
      { ok: false, text: "Business Intelligence" },
      { ok: false, text: "Priority support" },
    ],
  },
  {
    id: "basic",
    name: "Basic",
    price: "₱1,499",
    period: "per user / month",
    featured: true,
    blurb: "Everything a growing business needs, on one connected core.",
    features: [
      { ok: true, text: "Financial Accounting & live close" },
      { ok: true, text: "Human Capital management" },
      { ok: true, text: "Sales & Operations + Mobile POS" },
      { ok: true, text: "Inventory across locations" },
      { ok: true, text: "Unified dashboard" },
      { ok: true, text: "Up to 25 user seats" },
      { ok: false, text: "Payroll & PH compliance" },
      { ok: false, text: "Procurement & 3-way matching" },
      { ok: false, text: "Dedicated success manager" },
    ],
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "Custom",
    period: "contact sales",
    featured: false,
    blurb: "The whole ecosystem, multi-entity, with a team behind you.",
    features: [
      { ok: true, text: "All seven business modules" },
      { ok: true, text: "Payroll & PH compliance" },
      { ok: true, text: "Procurement & 3-way matching" },
      { ok: true, text: "Multi-entity consolidation" },
      { ok: true, text: "Business Intelligence & reporting" },
      { ok: true, text: "Unlimited user seats" },
      { ok: true, text: "Approval controls & audit trail" },
      { ok: true, text: "Dedicated success manager" },
      { ok: true, text: "Priority support & SLA" },
    ],
  },
];

const Plans = () => {
  const rootRef = useRef(null);

  /* Footer uses AOS reveals — keep it initialised here since this page
     renders standalone (not inside Home.js). */
  useEffect(() => {
    AOS.init({ duration: 800, easing: "ease-out-cubic", once: true, offset: 80 });
    AOS.refresh();
  }, []);

  /* Fade cards in when they enter the viewport and back out when they leave —
     same IntersectionObserver pattern as Carousel.js. */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const els = root.querySelectorAll(".nz-reveal");

    const reduced =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || !("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("is-in"));
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle("is-in", entry.isIntersecting);
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -10% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

    const handleLoginClick = () => {
    window.location.href = "http://ciam.noser.com.ph";
  };

  return (
    <React.Fragment>
      <Header />

      <section className="nz-plans" aria-label="Subscription plans" ref={rootRef}>
        <div className="nz-plans__grid" aria-hidden="true" />

        <div className="nz-plans__inner">
          <span className="nz-plans__eyebrow nz-reveal">Subscription plans</span>
          <h1 className="nz-plans__title nz-reveal" style={{ transitionDelay: "60ms" }}>
            One platform.
            <br />
            Pick the slice you need today.
          </h1>
          <p className="nz-plans__lead nz-reveal" style={{ transitionDelay: "120ms" }}>
            Every plan runs on the same connected core — so when you're ready for the next
            module, it's a switch you flip, never another integration project.
          </p>

          <div className="nz-plans__cards">
            {PLANS.map((p, i) => (
              <article
                key={p.id}
                className={`nz-plans__card nz-card nz-glass nz-reveal ${
                  p.featured ? "is-featured" : ""
                }`}
                style={{ transitionDelay: `${i * 90}ms` }}
              >
                {p.featured && <span className="nz-plans__badge">Most popular</span>}

                <h3 className="nz-plans__name">{p.name}</h3>
                <div className="nz-plans__price">
                  <strong>{p.price}</strong>
                  <span>{p.period}</span>
                </div>
                <p className="nz-plans__blurb">{p.blurb}</p>

                <ul className="nz-plans__features">
                  {p.features.map((f) => (
                    <li key={f.text} className={f.ok ? "is-on" : "is-off"}>
                      <span className="nz-plans__tick">{f.ok ? <Check /> : <Cross />}</span>
                      {f.text}
                    </li>
                  ))}
                </ul>

                <Link 
                  // to={REGISTER_PATH} 
                  onClick={handleLoginClick}
                  className="nz-pill nz-plans__cta">
                  Subscribe
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </React.Fragment>
  );
};

export default Plans;
